import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/** Load + manage a task's core data (task, photos, messages) and Stripe return polling. */
export default function useTaskData({ taskId, sessionId, onSessionConsumed }) {
  const [task, setTask] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [t, p, m] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/photos`),
        api.get(`/tasks/${taskId}/messages`),
      ]);
      setTask(t.data);
      setPhotos(p.data);
      setMessages(m.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [taskId]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll Stripe checkout status after redirect
  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Payment confirmed — escrow funded.");
          onSessionConsumed?.();
          await load();
          return;
        }
      } catch (err) {
        // Transient — retried below
        console.warn("useTaskData: Stripe status poll failed (retrying)", err);
      }
      attempts += 1;
      if (attempts < 6 && !cancelled) setTimeout(poll, 2000);
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, load, onSessionConsumed]);

  return { task, setTask, photos, setPhotos, messages, setMessages, error, reload: load };
}
